//core
import React from 'react';
import { matchSorter } from 'match-sorter';
import { useFormContext } from 'react-hook-form';

//MUI
import {
  Box,
  Button,
  FormHelperText,
  IconButton,
  Input,
  MenuItem,
  Popover,
  styled,
} from '@mui/material';
import {
  ArrowDropDown,
  ArrowDropUp,
  CloseOutlined,
  Search,
} from '@mui/icons-material';

//Components
import { usePopOverState } from '../../console/agencies/quotes/PrimeX/customizeQuote/components/DatePicker';
import { CheckBox } from '../inputs/Checkbox';
import type { SelectOption } from '../../types/components/common';
import { Show } from '../Show';

/**
 * A Reusable multi select component that suports searching by default.
 *
 * @prop {value} - an array of strings that represent a given form fields value in state
 * @prop {name} - The name under which the form field is registered.
 * @prop {options} - An array of Select options to be shown.
 * @prop {isSearchable} - An optional boolean that is defaulted to true. When true a search bar is rendered in the dropdown.
 * @prop {onChange} - An optional callback to handle field chnages however the dev needs. This component will attempt to update the value in React hook form's state if the context is present.
 * @example
 *  <MultiSelect
 *  value={['Example1', 'Example2']}
 *  name="example"
 *  options={[{label: 'Example1', value: 'Example1' }, {label: 'Example2', value: 'Example2' }]}
 *  isSearchable={false}
 *  />
 */

type MultiSelectProps = {
  readonly value: string[];
  readonly name: string;
  readonly options: SelectOption[];
  readonly isSearchable?: boolean;
  readonly onChange?: (value: string[]) => void;
  readonly showSelectAll?: boolean;
  readonly renderCustomAction?: () => React.ReactNode;
  /**
   * Show the selected options' labels on the closed control instead of their raw values.
   *
   * <p>Opt-in rather than the default. The closed control has always rendered the values, which
   * reads fine where a value is itself human-readable -- state codes select as "AL, AK" -- and
   * switching those callers to full names would change screens this component has no business
   * touching. It reads as nothing at all where the value is an opaque id: a vendor picker showed
   * a row of truncated UUIDs, so an adjuster could not tell who a note was about to be shared
   * with.
   */
  readonly renderSelectedLabels?: boolean;
  /**
   * Id of the element labelling this control.
   *
   * <p>Without it the button is announced by its contents alone, which are
   * empty until something is picked -- an unnamed control. Given it, the name
   * becomes the label followed by the selection, so neither is lost.
   */
  readonly labelId?: string;
  /**
   * Id for the button itself.
   *
   * <p>What a label's `htmlFor` binds to. A button is a labelable element, so
   * this is what makes the label clickable the way a text field's is.
   *
   * <p>Deliberately not defaulted to `name`: the popover already claims that id.
   */
  readonly id?: string;
};

const MultiSelect = ({
  name,
  options,
  isSearchable = true,
  showSelectAll = false,
  onChange,
  value,
  renderCustomAction,
  renderSelectedLabels = false,
  labelId,
  id,
}: MultiSelectProps) => {
  const popOverState = usePopOverState();
  const buttonRef = React.useRef<HTMLButtonElement | null>(null);
  const [popOverWidth, setPopOverWidth] = React.useState(500);
  const [searchTerm, setSearchTerm] = React.useState('');
  const formContext = useFormContext();

  React.useEffect(() => {
    if (buttonRef.current) {
      setPopOverWidth(buttonRef.current.offsetWidth);
    }
  }, [buttonRef?.current?.offsetWidth]);

  React.useEffect(() => {
    if (!popOverState.open) {
      setSearchTerm('');
    }
  }, [popOverState.open]);

  const handleOptionSelect = (optionValue: string) => {
    let nextValues;
    if (value.includes(optionValue)) {
      nextValues = value.filter((option) => option !== optionValue);
    } else {
      nextValues = [...value, optionValue];
    }
    if (formContext) {
      formContext.setValue(name, nextValues);
    }

    onChange?.(nextValues);
  };

  const allSelected = options.length === value.length;
  const toggleSelectAll = () => {
    let nextValues: string[] = [];
    if (allSelected) {
      nextValues = [];
    } else {
      nextValues = options.map((option) => option.value);
    }

    if (formContext) {
      formContext.setValue(name, nextValues);
    }
    onChange?.(nextValues);
  };

  const filteredOptions = React.useMemo(() => {
    return matchSorter(options, searchTerm, {
      keys: ['label'],
    });
  }, [options, searchTerm]);

  // Optional all the way down. The chain used to stop after formContext, so a
  // provider given a context without formState -- which is easy to do by
  // spreading the rest of useForm while holding formState back -- crashed the
  // whole form rather than merely losing a validation message.
  const fieldError = formContext?.formState?.errors?.[name]?.message as
    | string
    | undefined;

  // A value with no matching option keeps its raw form rather than vanishing: better a visible
  // oddity than a selection that silently looks like it was never made.
  const labelsByValue = new Map(
    options.map((option) => [option.value, option.label])
  );
  const selectedText = renderSelectedLabels
    ? value.map((item) => labelsByValue.get(item) ?? item).join(', ')
    : value.join(', ');

  /*
   * The label, then the element holding the selection.
   *
   * <p>`aria-labelledby` displaces a control's contents rather than adding to
   * them, so naming the button by the label alone would announce the field's
   * purpose and drop who a note is about to reach -- the one thing a writer
   * cannot revise after saving. Pointing at the value element as well puts it
   * back. MUI composes its select's name by self-reference instead; that relies
   * on a cycle rule implementations disagree on, and a plain second id does not.
   */
  const valueId = `${name}-value`;
  const labelledBy = labelId ? `${labelId} ${valueId}` : undefined;

  return (
    <Box width="100%">
      <StyledButton
        id={id}
        onClick={popOverState.onOpen}
        ref={buttonRef}
        aria-labelledby={labelledBy}
      >
        <StyledSpan id={valueId}>{selectedText}</StyledSpan>
        {popOverState.open ? <ArrowDropUp /> : <ArrowDropDown />}
      </StyledButton>
      <Popover
        id={name}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        sx={{
          maxHeight: '25rem',
          '& .MuiPopover-paper': {
            width: popOverWidth,
          },
        }}
        {...popOverState}
      >
        <>
          {renderCustomAction?.()}
          <Show when={isSearchable}>
            <SearchModule onChange={setSearchTerm} value={searchTerm} />
          </Show>
          <Show when={showSelectAll}>
            <MenuItem
              key="select-all"
              onClick={toggleSelectAll}
              sx={{ whiteSpace: 'normal' }}
            >
              <CheckBox checked={allSelected} />
              {allSelected ? 'Deselect All' : 'Select All'}
            </MenuItem>
          </Show>
          {filteredOptions.map((option) => {
            return (
              <MenuItem
                key={option.value}
                value={option.value}
                onClick={() => handleOptionSelect(option.value)}
                sx={{
                  whiteSpace: 'normal',
                }}
              >
                <CheckBox checked={value.includes(option.value)} />
                {option.label}
              </MenuItem>
            );
          })}
        </>
      </Popover>
      <Show when={Boolean(fieldError)}>
        <FormHelperText style={{ margin: 0 }}>{fieldError}</FormHelperText>
      </Show>
    </Box>
  );
};

export default MultiSelect;

type SearchModuleProps = {
  value: string;
  onChange: (input: string) => void;
};

const SearchModule = ({ value, onChange }: SearchModuleProps) => {
  return (
    <Box position="relative" pt="0.15rem">
      <Input
        placeholder="Search..."
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoFocus
        sx={{
          '& .MuiInput-input': {
            border: 'none',
          },
          ' & .MuiInput-input:focus': {
            background: (theme) => theme.palette.background.active,
          },
          opacity: (theme) => (theme.palette.mode === 'dark' ? 1 : 0.8),
        }}
      />

      <Box
        position="absolute"
        top="50%"
        right={3}
        style={{ transform: 'translateY(-50%)' }}
      >
        <IconButton onClick={() => onChange('')}>
          {value === '' ? <Search /> : <CloseOutlined />}
        </IconButton>
      </Box>
    </Box>
  );
};

//styling
const StyledButton = styled(Button)(({ theme }) => {
  return {
    width: '100%',
    justifyContent: 'space-between',
    border: `1px solid ${theme.palette.mode === 'dark' ? '#fff' : '#9aa0ae'}`,
    color: theme.palette.text.contrastText,
    textTransform: 'capitalize',
    paddingTop: '0.6rem',
    paddingBottom: '0.6rem',
  };
});

const StyledSpan = styled('span')(() => {
  return {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    width: '85%',
    display: 'inline-block',
    textAlign: 'left',
    paddingLeft: '0.25rem',
  };
});
